import { Text, VStack } from '@chakra-ui/react';
import { SystemStorageForm } from '@components/cockpit/panels/systemParameters/systemStorage/SystemStorageForm';
import { SystemStorageOverview } from '@components/cockpit/panels/systemParameters/systemStorage/SystemStorageOverview';
import { useAppSelector } from '@hooks/redux.hooks';
import { hasRole, selectActiveUser } from '@redux/authent/authent.selectors';
import { RootState } from '@redux/store';
import { skipToken } from '@reduxjs/toolkit/query';
import { useGetStorageConfigurationQuery } from '@services/c2/c2.api';
import { convertDuration } from '@utils/date.utils';
import { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { RoleEnum } from '@/types/authent/roles.types';

export type SystemParamFormFields = {
  enabledSituationStorage: boolean;
  situationStorageDuration: number;
  enabledSensorRawStorage: boolean;
  sensorRawStorageDuration: number;
  enabledLadVideoStreamStorage: boolean;
  ladVideoStreamStorageDuration: number;
};

const DEFAULT_SITUATION_STORAGE_DURATION_SECONDS = 7 * 24 * 3600; // 7 days
const DEFAULT_SENSOR_RAW_STORAGE_DURATION_SECONDS = 7 * 24 * 3600; // 7 days
const DEFAULT_NIMBLE_CONFIGURATION_STORAGE_AGE = 12; // 12 hours

const EMPTY_FORM: SystemParamFormFields = {
  enabledSituationStorage: true,
  situationStorageDuration: 7,
  enabledSensorRawStorage: true,
  sensorRawStorageDuration: 7,
  enabledLadVideoStreamStorage: true,
  ladVideoStreamStorageDuration: 12,
};

export function SystemStorage() {
  const hasEditStorageRole = useAppSelector((state: RootState) => hasRole(state, RoleEnum.EDIT_STORAGE));

  const { activeSite } = useAppSelector(selectActiveUser);
  const { data: storageConfiguration, isLoading: isLoadingGet } = useGetStorageConfigurationQuery(
    activeSite?.code ?? skipToken,
  );
  const [cachedValues, setCachedValues] = useState<SystemParamFormFields>(EMPTY_FORM);

  useEffect(() => {
    setCachedValues({
      enabledSituationStorage: storageConfiguration?.replayConfiguration?.enabledSituationStorage ?? true,
      situationStorageDuration: convertDuration(
        storageConfiguration?.replayConfiguration?.situationStorageDuration ??
          DEFAULT_SITUATION_STORAGE_DURATION_SECONDS,
        'seconds',
        'days',
      ),
      enabledSensorRawStorage: storageConfiguration?.sensorConfiguration?.enabledSensorRawStorage ?? true,
      sensorRawStorageDuration: convertDuration(
        storageConfiguration?.sensorConfiguration?.sensorRawStorageDuration ??
          DEFAULT_SENSOR_RAW_STORAGE_DURATION_SECONDS,
        'seconds',
        'days',
      ),
      enabledLadVideoStreamStorage: storageConfiguration?.videoConfiguration?.enabledLadVideoStreamStorage ?? true,
      ladVideoStreamStorageDuration:
        storageConfiguration?.videoConfiguration?.ladVideoStreamStorageDuration ??
        DEFAULT_NIMBLE_CONFIGURATION_STORAGE_AGE,
    });
  }, [storageConfiguration]);

  return (
    <VStack>
      <Text alignSelf="start" fontSize="16px">
        <FormattedMessage id="components.systemParameters.storageDurations" />
      </Text>
      {hasEditStorageRole ? (
        <SystemStorageForm
          storageConfiguration={storageConfiguration}
          isLoadingGet={isLoadingGet}
          cachedValues={cachedValues}
        />
      ) : (
        <SystemStorageOverview cachedValues={cachedValues} />
      )}
    </VStack>
  );
}
